/**
 * BridgeModule — A3 module 1 (flagship): the additive EBITDA component bridge,
 * Revenue − COGS − OPEX = EBITDA, for the scoped site's latest served month.
 * Composition only (CD-7): the pivot + reconcile live in bridgeModel; the chart is
 * the kit Waterfall. The reconcile verdict is loud (constitution §3 — a bridge whose
 * legs don't sum to the headline is a bug, not a footnote), a DQ-tainted leg wears
 * its flag, and an unserved component is an honest gap rather than a zeroed bar.
 */

import React from 'react'
import { CheckCircle2, XCircle } from 'lucide-react'
import Waterfall from '../components/kit/charts/Waterfall'
import TrustChip from '../components/kit/TrustChip'
import InfoBanner from '../components/InfoBanner'
import { dqBadge, periodHeading } from '../components/kit/cellFormat'
import { buildEbitdaBridge, type BridgeModel } from '../components/kit/bridgeModel'
import { type Scope } from '../hooks/useScope'
import type { ResolveResponse } from '../types/metrics'

const gbpk = (displayK: number): string =>
  `£${Math.abs(displayK) < 100 ? displayK.toFixed(1) : displayK.toFixed(0)}k`

function ReconcileVerdict({ model }: { model: BridgeModel }): React.JSX.Element {
  const sum = model.legSumRaw === null ? null : model.legSumRaw / 1000
  const head = model.headlineDisplay
  if (model.reconciles && sum !== null && head !== null) {
    return (
      <div role="status" data-testid="bridge-reconcile" data-reconciles="true" className="flex items-center gap-2 rounded-xl border border-good bg-good-bg px-3 py-1.5 text-xs font-medium text-good">
        <CheckCircle2 size={14} className="flex-shrink-0" />
        <span>Reconciles — Revenue − COGS − OPEX = {gbpk(sum)} = {model.headlineLabel} {gbpk(head)}</span>
      </div>
    )
  }
  const delta = sum !== null && head !== null ? sum - head : null
  return (
    <div role="alert" data-testid="bridge-reconcile" data-reconciles="false" className="flex items-start gap-2 rounded-xl border-2 border-bad bg-bad-bg px-3 py-2.5">
      <XCircle size={18} className="mt-0.5 flex-shrink-0 text-bad" />
      <div>
        <p className="text-sm font-bold uppercase tracking-wide text-bad">
          {sum === null || head === null
            ? 'Cannot reconcile — a component is unserved'
            : `Does not reconcile — Σ legs ${gbpk(sum)} ≠ ${model.headlineLabel} ${gbpk(head)} (Δ ${gbpk(delta as number)})`}
        </p>
        <p className="mt-0.5 text-xs text-bad">
          A bridge whose legs don&apos;t sum to the headline is a bug, not a footnote (constitution §3).
        </p>
      </div>
    </div>
  )
}

function LegLegend({ model }: { model: BridgeModel }): React.JSX.Element | null {
  const tainted = [...model.legs, ...(model.headlineCell ? [{ id: model.headlineId, label: model.headlineLabel, dqFlags: model.headlineCell.dq_flags }] : [])]
    .filter((leg) => dqBadge(leg.dqFlags) !== null)
  if (tainted.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-mut" data-testid="bridge-taint">
      <span className="font-mono text-[10px] uppercase tracking-[.08em] text-mut2">envelope carry-through</span>
      {tainted.map((leg) => {
        const badge = dqBadge(leg.dqFlags)!
        return (
          <span key={leg.id} className="inline-flex items-center gap-1.5">
            <span className="font-semibold text-ink">{leg.label}</span>
            <TrustChip tone={badge.tone} label={badge.label} title={leg.dqFlags.join(' · ')} />
          </span>
        )
      })}
    </div>
  )
}

/** Honest fallback when a waterfall can't draw the bridge (a loss month or incoherent
 *  components take the running total below zero): the exact signed legs and the derived
 *  Σ against the served headline — the same numbers, without a clipped chart. */
function LegsList({ model }: { model: BridgeModel }): React.JSX.Element {
  const derived = model.legSumRaw === null ? null : model.legSumRaw / 1000
  return (
    <div className="border border-line rounded-xl bg-panel divide-y divide-line2" data-testid="bridge-legs">
      {model.legs.map((leg) => (
        <div key={leg.id} className="flex items-center justify-between px-4 py-2 text-[13px]">
          <span className="text-mut">{leg.label}</span>
          <span className={`font-mono ${leg.sign < 0 ? 'text-bad' : 'text-ink'}`}>
            {leg.sign < 0 ? '−' : '+'}
            {gbpk(Math.abs(leg.display as number))}
          </span>
        </div>
      ))}
      <div className="flex items-center justify-between px-4 py-2 text-[13px] font-semibold">
        <span className="text-ink">Σ legs → {model.headlineLabel}</span>
        <span className="font-mono text-ink">
          {derived === null ? '—' : gbpk(derived)} vs {model.headlineDisplay === null ? '—' : gbpk(model.headlineDisplay)}
        </span>
      </div>
    </div>
  )
}

function BridgeChart({ model }: { model: BridgeModel }): React.JSX.Element {
  if (model.hasGap) {
    const missing = model.legs.filter((l) => l.raw === null).map((l) => l.label).join(', ') || model.headlineLabel
    return (
      <InfoBanner
        variant="warning"
        message={`Bridge incomplete — ${missing} unserved for ${model.period ? periodHeading(model.period) : 'this period'}. An unserved leg is a gap, never a zero; the bridge is not drawn until every leg serves.`}
      />
    )
  }
  if (!model.chartable) return <LegsList model={model} />
  const steps = [
    ...model.legs.map((leg) => ({ label: leg.label, v: leg.display as number })),
    { label: model.headlineLabel, v: model.headlineDisplay as number, type: 'total' as const },
  ]
  return (
    <div className="border border-line rounded-xl bg-panel px-3 py-4">
      <Waterfall steps={steps} fmt={gbpk} />
    </div>
  )
}

export default function BridgeModule({ scope, resolve }: { scope: Scope; resolve: ResolveResponse }): React.JSX.Element {
  const model = buildEbitdaBridge(resolve, scope.site)
  const servedLegs = model.legs.filter((leg) => leg.raw !== null)

  if (model.period === null || servedLegs.length === 0) {
    return (
      <InfoBanner
        variant="info"
        message={`No P&L components served for ${scope.site} in ${scope.periodFrom} → ${scope.periodTo}. The EBITDA bridge needs Revenue, COGS and OPEX.`}
      />
    )
  }

  return (
    <div className="space-y-3" data-testid="bridge-module">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[15px] font-bold text-ink">EBITDA bridge · {model.site} · {periodHeading(model.period)}</h3>
        <span className="font-mono text-[10.5px] text-mut2">Revenue − COGS − OPEX = EBITDA</span>
      </div>
      <ReconcileVerdict model={model} />
      <BridgeChart model={model} />
      <LegLegend model={model} />
    </div>
  )
}
